/**
 * Incident Editor Dialog - SLA Tab (PROJ-007)
 * Displays SLA timers with visual indicators, progress bars, and breach alerts
 */

import { useEffect, useState } from 'react';
import { Clock, AlertTriangle, CheckCircle2, XCircle, Loader2, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { listSlaTimers, getSlaStats } from '@/lib/slaApi';
import type { SlaTimer, SlaStats } from '@/types/sla';
import { formatRemainingTime, getSlaStatusColor, getSlaStatusVariant, SLA_STAGE_LABELS, SLA_STAGE_DESCRIPTIONS } from '@/types/sla';

interface SlaTabProps {
  incidentId: string | null;
}

export function SlaTab({ incidentId }: SlaTabProps) {
  const [timers, setTimers] = useState<SlaTimer[]>([]);
  const [stats, setStats] = useState<SlaStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!incidentId) {
      setLoading(false);
      return;
    }

    loadSlaData();
    // Refresh every 30 seconds
    const interval = setInterval(loadSlaData, 30000);
    return () => clearInterval(interval);
  }, [incidentId]);

  async function loadSlaData() {
    if (!incidentId) return;

    try {
      setLoading(true);
      setError(null);

      const [timersData, statsData] = await Promise.all([
        listSlaTimers(incidentId),
        getSlaStats(incidentId),
      ]);

      setTimers(timersData);
      setStats(statsData);
    } catch (err) {
      console.error('Failed to load SLA data:', err);
      setError('No se pudieron cargar los datos de SLA');
    } finally {
      setLoading(false);
    }
  }

  // Calculate progress percentage (0-100)
  const calculateProgress = (timer: SlaTimer): number => {
    if (timer.breached) return 100;
    if (timer.remainingHours <= 0) return 100;

    // Calculate progress based on warning threshold (2 hours)
    // If more than 2 hours remaining: green zone (0-70%)
    // If less than 2 hours remaining: yellow/red zone (70-100%)
    const warningThreshold = 2;

    if (timer.remainingHours > warningThreshold) {
      // Green zone - progress slowly
      return Math.min(70, (1 - timer.remainingHours / 24) * 70);
    } else {
      // Warning/critical zone - show urgency
      const criticalProgress = 70 + ((warningThreshold - timer.remainingHours) / warningThreshold) * 30;
      return Math.min(100, Math.max(70, criticalProgress));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!incidentId) {
    return (
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          Guarda el incidente primero para ver los SLA timers.
        </AlertDescription>
      </Alert>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <XCircle className="h-4 w-4" />
        <AlertTitle>Error</AlertTitle>
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  if (!timers || timers.length === 0) {
    return (
      <Alert>
        <Clock className="h-4 w-4" />
        <AlertDescription>
          No hay SLA timers configurados para este incidente.
        </AlertDescription>
      </Alert>
    );
  }

  const breachedTimers = timers.filter(t => t.breached);
  const warningTimers = timers.filter(t => t.isWarning && !t.breached);
  const onTrackTimers = timers.filter(t => !t.isWarning && !t.breached);

  return (
    <div className="space-y-6">
      {/* Overall Status Alert */}
      {breachedTimers.length > 0 && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>SLA Vencido</AlertTitle>
          <AlertDescription>
            {breachedTimers.length} {breachedTimers.length === 1 ? 'timer vencido' : 'timers vencidos'}.
            Se requiere acción inmediata.
          </AlertDescription>
        </Alert>
      )}

      {breachedTimers.length === 0 && warningTimers.length > 0 && (
        <Alert>
          <AlertTriangle className="h-4 w-4 text-yellow-600" />
          <AlertTitle>Advertencia SLA</AlertTitle>
          <AlertDescription>
            {warningTimers.length} {warningTimers.length === 1 ? 'timer cerca' : 'timers cerca'} de vencer.
            Tiempo crítico.
          </AlertDescription>
        </Alert>
      )}

      {breachedTimers.length === 0 && warningTimers.length === 0 && (
        <Alert className="border-green-500 bg-green-50 dark:bg-green-950">
          <CheckCircle2 className="h-4 w-4 text-green-600" />
          <AlertTitle>SLA en Regla</AlertTitle>
          <AlertDescription>
            Todos los timers están dentro de los límites establecidos.
          </AlertDescription>
        </Alert>
      )}

      {/* Stats Summary */}
      {stats && (
        <div className="grid grid-cols-3 gap-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium">Total Timers</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.total}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium">Vencidos</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-red-600">{stats.breached}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium">En Regla</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">{stats.onTrack}</div>
            </CardContent>
          </Card>
        </div>
      )}

      <Separator />

      {/* Timer Cards */}
      <div className="space-y-4">
        {timers.map((timer) => {
          const statusVariant = getSlaStatusVariant(timer);
          const statusColor = getSlaStatusColor(timer);
          const progress = calculateProgress(timer);

          return (
            <Card key={timer.id} className={timer.breached ? 'border-red-500' : timer.isWarning ? 'border-yellow-500' : ''}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Clock className={`h-5 w-5 ${statusColor}`} />
                    <CardTitle className="text-base">
                      {SLA_STAGE_LABELS[timer.stage]}
                    </CardTitle>
                  </div>
                  <Badge 
                    variant={statusVariant === 'warning' ? 'secondary' : statusVariant}
                    className={statusVariant === 'warning' ? 'bg-yellow-500 hover:bg-yellow-600 text-white border-transparent' : ''}
                  >
                    {timer.breached ? 'VENCIDO' : timer.isWarning ? 'ADVERTENCIA' : 'EN REGLA'}
                  </Badge>
                </div>
                <CardDescription>
                  {SLA_STAGE_DESCRIPTIONS[timer.stage]}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Progress Bar */}
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Progreso</span>
                    <span className={`font-medium ${timer.breached ? 'text-red-600' : timer.isWarning ? 'text-yellow-600' : 'text-green-600'}`}>
                      {formatRemainingTime(timer.remainingHours)}
                      {timer.breached && ' (Vencido)'}
                      {!timer.breached && ' restante'}
                    </span>
                  </div>
                  <Progress
                    value={progress}
                    className={timer.breached ? 'bg-red-200' : timer.isWarning ? 'bg-yellow-200' : 'bg-green-200'}
                  />
                </div>

                {/* Timer Details */}
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Objetivo:</span>
                    <div className="font-medium">
                      {new Date(timer.targetAt).toLocaleString('es-ES', {
                        dateStyle: 'short',
                        timeStyle: 'short',
                      })}
                    </div>
                  </div>

                  {timer.breached && timer.breachedAt && (
                    <div>
                      <span className="text-muted-foreground">Vencido en:</span>
                      <div className="font-medium text-red-600">
                        {new Date(timer.breachedAt).toLocaleString('es-ES', {
                          dateStyle: 'short',
                          timeStyle: 'short',
                        })}
                      </div>
                    </div>
                  )}
                </div>

                {/* Breach Alert */}
                {timer.breached && (
                  <Alert variant="destructive" className="mt-2">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription className="text-xs">
                      Este SLA ha sido vencido. Se requiere acción correctiva inmediata.
                    </AlertDescription>
                  </Alert>
                )}

                {/* Warning Alert */}
                {timer.isWarning && !timer.breached && (
                  <Alert className="mt-2 border-yellow-500 bg-yellow-50 dark:bg-yellow-950">
                    <AlertTriangle className="h-4 w-4 text-yellow-600" />
                    <AlertDescription className="text-xs">
                      Tiempo crítico. Menos de 2 horas para vencimiento.
                    </AlertDescription>
                  </Alert>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Refresh Info */}
      <div className="text-xs text-muted-foreground text-center">
        Los datos se actualizan automáticamente cada 30 segundos
      </div>
    </div>
  );
}
